/**
 * **Moving a session on the grid** — which sessions may be moved, what a move sends, and how the
 * grid draws a move while the server has not answered yet.
 *
 * The server's reschedule rules (calendar-events R9–R11, `CalendarEventProcessor.RescheduleAsync`):
 * a finished, cancelled or no-show session never moves (R11), nor does a workout the client has
 * already started (R10: a late start applies instead). A booking whose day has passed may not be
 * carried into the future either. The grid offers a drag only where all of that is certainly
 * true — a session still ahead of its hour, not yet begun — and leaves every rarer refusal (a plan
 * day already taken, a plan that ended) to the server, whose reason is shown when it says no.
 *
 * A drag moves the start and keeps the length; a resize keeps the start and moves the end, and only
 * then is the length sent (`durationMinutes`, 5–480). Both land on the grid's quarter hour.
 *
 * Pure: no React, no DOM, no query hook, no i18next.
 */

import type { TrainWithMeModulesTrainingDtosRequestsRescheduleCalendarEventRequest as RescheduleRequest } from '~/shared/api/generated/model';

import type { GridSession } from './bookingCalendar';
import { isLiveAt, type BookingSession } from './bookingSession';
import type { TimeSpan } from './busyTime';
import { draftSpan, isBookableLength } from './sessionDraft';
import { clockOf, minutesOfTime, paddedClock, wallReading } from './trainerClock';

const MINUTE_MS = 60_000;

/** Whether the grid lets the trainer drag or resize this session. */
export function canMove(session: BookingSession, nowMs: number): boolean {
  return (
    !session.voided &&
    session.hasHour &&
    session.status === 'scheduled' &&
    !session.emptySlot &&
    !isLiveAt(session, nowMs) &&
    session.slotMs !== null &&
    session.slotMs > nowMs
  );
}

/** Why a move cannot be sent — said before any request is made. */
export type MoveProblem = 'midnight' | 'duration' | 'past';

export type MoveSubmission =
  | { readonly ok: true; readonly body: RescheduleRequest }
  | { readonly ok: false; readonly problem: MoveProblem };

/**
 * The body of `POST /calendar-event/{id}/reschedule` for `session` moved to `target`, read on the
 * trainer's clock and sent WITH that clock's zone (see `sessionDraft.createRequest` for why it is
 * never left out). The length goes only when it changed — a plain move leaves it to the server.
 *
 * A target already behind `nowMs` is refused here though the server would take it: a booking
 * carried into the past is a record, not an appointment.
 */
export function moveSubmission(
  session: BookingSession,
  target: TimeSpan,
  zone: string,
  nowMs: number,
): MoveSubmission {
  const start = wallReading(target.startMs, zone);
  const minutes = Math.round((target.endMs - target.startMs) / MINUTE_MS);
  if (start.minutes === 0) return { ok: false, problem: 'midnight' };
  if (!isBookableLength(minutes)) return { ok: false, problem: 'duration' };
  if (target.startMs < nowMs) return { ok: false, problem: 'past' };
  return {
    ok: true,
    body: {
      localDate: start.day,
      localTime: paddedClock(start.minutes),
      zoneId: zone,
      ...(minutes === session.durationMinutes ? {} : { durationMinutes: minutes }),
    },
  };
}

/** What the "Przełóż" form holds: three fields as typed. */
export interface MoveFields {
  readonly day: string;
  readonly time: string;
  readonly length: string;
}

/** Which field a refused move is about. */
export type MoveField = 'day' | 'time' | 'length';

export type MoveFieldsReading =
  | { readonly ok: true; readonly target: TimeSpan }
  | {
      readonly ok: false;
      readonly problem: MoveProblem | 'noDate' | 'noTime';
      readonly field: MoveField;
    };

/**
 * The "Przełóż" form read: the span its fields name, or the first field in the way and why. The
 * same `moveSubmission` rules a drop meets, plus the two a form alone can break — an empty date
 * or time.
 */
export function readMoveFields(
  session: BookingSession,
  fields: MoveFields,
  zone: string,
  nowMs: number,
): MoveFieldsReading {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(fields.day))
    return { ok: false, problem: 'noDate', field: 'day' };
  const minutes = minutesOfTime(fields.time);
  if (minutes === null) return { ok: false, problem: 'noTime', field: 'time' };
  const length = fields.length.trim() === '' ? Number.NaN : Number(fields.length);
  if (!isBookableLength(length)) return { ok: false, problem: 'duration', field: 'length' };
  const target = draftSpan(
    { day: fields.day, startMinutes: minutes, durationMinutes: length },
    zone,
  );
  const submission = moveSubmission(session, target, zone, nowMs);
  if (!submission.ok) return { ok: false, problem: submission.problem, field: 'time' };
  return { ok: true, target };
}

/** A move sent and not yet read back: where the block stood on the server, and where it went. */
export interface PendingMove {
  readonly from: TimeSpan;
  readonly to: TimeSpan;
  /** Which drop this is — a later drop of the same block replaces it, an earlier settle may not. */
  readonly token: number;
}

/**
 * The grid with the moves the calendar on screen does not show yet drawn where they were dropped,
 * so a release is not answered by the block snapping home and jumping again a round trip later.
 *
 * A move stands only while the server's reading of the block is still the one it was dropped
 * FROM: once a read shows the block anywhere else — where it was moved, or wherever somebody else
 * put it — that read is newer than the drop, and it wins. A refetch that fails therefore leaves
 * the block where it was dropped, not back where it started. Only where the block stands changes.
 */
export function withPendingMoves(
  grid: readonly GridSession[],
  moves: ReadonlyMap<number, PendingMove>,
  zone: string,
): GridSession[] {
  if (moves.size === 0) return [...grid];
  return grid.map((entry) => {
    const move = moves.get(entry.session.id);
    if (
      move === undefined ||
      entry.session.startMs !== move.from.startMs ||
      entry.session.endMs !== move.from.endMs
    ) {
      return entry;
    }
    const { to } = move;
    const start = wallReading(to.startMs, zone);
    const end = wallReading(to.endMs, zone);
    const startClock = clockOf(start.minutes);
    const endClock = clockOf(end.minutes);
    return {
      ...entry,
      session: {
        ...entry.session,
        day: start.day,
        startMs: to.startMs,
        endMs: to.endMs,
        durationMinutes: Math.round((to.endMs - to.startMs) / MINUTE_MS),
        startClock,
        endClock,
        timeLabel: `${startClock}–${endClock}`,
      },
    };
  });
}
